
import { RefObject } from 'react';

/**
 * Hook to provide video control actions
 */
export function useVideoActions(
  videoRef: RefObject<HTMLVideoElement>,
  containerRef: RefObject<HTMLDivElement>,
  isMuted: boolean,
  setIsMuted: (muted: boolean) => void,
  setVolume: (volume: number) => void
) {
  // Play/Pause toggle handler
  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      // Set volume to 100% and unmute when playing
      video.volume = 1;
      video.muted = false;
      setVolume(1);
      setIsMuted(false);
      video.play().catch(err => console.error('Play failed:', err));
    } else {
      video.pause();
    }
  };

  // Skip forward 10 seconds
  const handleSkipForward = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.min(video.duration, video.currentTime + 10);
  };

  // Skip back 10 seconds
  const handleSkipBack = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, video.currentTime - 10);
  };

  // Toggle mute. Como o player inicializa com volume=0 quando muted=true,
  // desmutar exige também restaurar o volume para 1 — senão o áudio fica em 0.
  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    const shouldUnmute = video.muted || video.volume === 0;
    if (shouldUnmute) {
      video.muted = false;
      video.volume = 1;
      setIsMuted(false);
      setVolume(1);
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  };

  // Handle volume change
  const handleVolumeSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    
    if (newVolume === 0) {
      video.muted = true;
      setIsMuted(true);
    } else if (isMuted) {
      video.muted = false;
      setIsMuted(false);
    }
  };

  // Handle seek on progress bar click
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const position = (e.clientX - rect.left) / rect.width;
    video.currentTime = position * video.duration;
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      container.requestFullscreen();
    }
  };

  // Toggle Picture-in-Picture
  const togglePictureInPicture = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if ('pictureInPictureEnabled' in document) {
        if (document.pictureInPictureElement) {
          await document.exitPictureInPicture();
        } else {
          await video.requestPictureInPicture();
        }
      }
    } catch (error) {
      console.error('Picture-in-Picture error:', error);
    }
  };

  // Set playback speed
  const setPlaybackSpeed = (speed: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.playbackRate = speed;
  };

  return {
    togglePlay,
    handleSkipForward,
    handleSkipBack,
    toggleMute,
    handleVolumeSliderChange,
    handleSeek,
    toggleFullscreen,
    togglePictureInPicture,
    setPlaybackSpeed
  };
}
